// Peças compartilhadas das páginas de Ajustes (0.67, texto 05 D2): tudo encolhido.
// Escolha = uma linha com o valor atual e › (abre a lista, ✓ na atual); liga/desliga = linha com interruptor;
// botão de ação proporcional, numa linha só. Tudo segue o "Tamanho das caixas" (Ajustes > Geral).
import { useState, type ReactNode } from 'react';
import { Pressable, Switch, Text, View } from 'react-native';
import { ActionSheet, type Action } from '../ui';
import { C, alpha } from '../theme';

// 0.67+: a lógica de medidas mora em lib/caixas.ts (vale para o app inteiro); reexportada aqui.
export { medidasCaixas, useTamCaixas, useCaixas, caixas, type TamCaixas, type Caixas } from '../../lib/caixas';
import { useTamCaixas } from '../../lib/caixas';

/** Linha de escolha: título, valor atual e ›. */
export function LinhaEscolha({ icone, titulo, valor, dica, onPress, cor }: { icone: string; titulo: string; valor?: string; dica?: string; onPress: () => void; cor?: string }) {
  const m = useTamCaixas();
  return (
    <View>
      <Pressable onPress={onPress} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: m.linhaV, opacity: pressed ? 0.6 : 1 })}>
        <Text style={{ fontSize: 18 }}>{icone}</Text>
        <Text style={{ color: cor ?? C.text, flex: 1, fontWeight: '600' }}>{titulo}</Text>
        {!!valor && <Text style={{ color: C.sub, fontSize: 13, maxWidth: '55%' }} numberOfLines={1}>{valor}</Text>}
        <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>
      </Pressable>
      {!!dica && <Text style={{ color: C.mute, fontSize: 11, marginTop: -m.linhaV + 4, marginBottom: 6, marginLeft: 28 }}>{dica}</Text>}
    </View>
  );
}

/** Linha de liga/desliga com interruptor à direita. */
export function LinhaChave({ icone, titulo, valor, cor, dica, onMudar }: { icone: string; titulo: string; valor: boolean; cor: string; dica?: string; onMudar: (v: boolean) => void }) {
  const m = useTamCaixas();
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: Math.max(4, m.linhaV - 6) }}>
        <Text style={{ fontSize: 18 }}>{icone}</Text>
        <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{titulo}</Text>
        <Switch value={valor} trackColor={{ true: cor }} onValueChange={onMudar} />
      </View>
      {!!dica && <Text style={{ color: C.mute, fontSize: 11, marginTop: -2, marginBottom: 6, marginLeft: 28 }}>{dica}</Text>}
    </View>
  );
}

export const Divisor = () => <View style={{ height: 1, backgroundColor: C.line }} />;

/** Linha de escolha que já traz a lista: ao tocar abre as opções com ✓ na atual; ao escolher, volta. */
export function LinhaOpcoes<T extends string | number>({ icone, titulo, opcoes, valor, onEscolher, dica, rotulo }:
  { icone: string; titulo: string; opcoes: [T, string][]; valor: T; onEscolher: (v: T) => void; dica?: string; rotulo?: string }) {
  const [menu, setMenu] = useState<Action[] | null>(null);
  const atual = opcoes.find(o => o[0] === valor)?.[1] ?? '';
  return (<>
    <LinhaEscolha icone={icone} titulo={titulo} valor={rotulo ?? atual} dica={dica}
      onPress={() => setMenu(opcoes.map(([v, l]) => ({ label: (v === valor ? '✓ ' : '    ') + l, onPress: () => onEscolher(v) })))} />
    <ActionSheet title={titulo} actions={menu} onClose={() => setMenu(null)} />
  </>);
}

/** Bloco de opções (cartão) com um título pequeno em cima. */
export function Bloco({ titulo, children }: { titulo?: string; children: ReactNode }) {
  const m = useTamCaixas();
  return (
    <View style={{ gap: 6 }}>
      {!!titulo && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 10 }}>{titulo.toUpperCase()}</Text>}
      <View style={{ backgroundColor: C.card, borderRadius: m.raio, paddingHorizontal: m.padH - 2, paddingVertical: 4 }}>{children}</View>
    </View>
  );
}

/** Texto de explicação dentro de um bloco. */
export const Dica = ({ children }: { children: ReactNode }) => <Text style={{ color: C.sub, fontSize: 12, paddingVertical: 8 }}>{children}</Text>;

/** Botão de ação proporcional, numa linha só (no máximo do tamanho do título do cartão). */
export function BotaoAcao({ label, onPress, cor, cheio, disabled }: { label: string; onPress: () => void; cor: string; cheio?: boolean; disabled?: boolean }) {
  const m = useTamCaixas();
  return (
    <View style={{ flexDirection: 'row', paddingVertical: 6 }}>
      <Pressable disabled={disabled} onPress={onPress} style={({ pressed }) => ({ paddingVertical: m.tam === 'g' ? 9 : m.tam === 'm' ? 7 : 5, paddingHorizontal: 14, borderRadius: 18,
        borderWidth: 1, borderColor: disabled ? C.mute : cor, backgroundColor: cheio ? (disabled ? C.mute : cor) : pressed ? alpha(cor, 0.12) : 'transparent', opacity: pressed ? 0.8 : 1 })}>
        <Text numberOfLines={1} style={{ color: cheio ? '#fff' : disabled ? C.mute : cor, fontWeight: '700', fontSize: Math.min(14, m.titulo) }}>{label}</Text>
      </Pressable>
    </View>
  );
}
